import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import PageLoader from "../PageLoader";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import StevenButton from "../Common/StevenButton";
import PublicBettingConsensus from "../Common/PublicBettingConsensus";
import gameService from "../../services/gameService";
import apiClient from "../../services/apiClient";

const INJURY_ABBR = { "IR": "IR", "Out": "O", "Doubtful": "D", "Questionable": "Q", "Probable": "P" };
const INJURY_COLOR = { "IR": "#d32f2f", "Out": "#d32f2f", "Doubtful": "#d32f2f", "Questionable": "#ed6c02", "Probable": "#2e7d32" };

const teamLogoSrc = (teamName) => {
  if (!teamName) return null;
  const last = teamName.trim().split(" ").pop();
  return `/logos/${last}.png`;
};

const rankColor = (rank) => {
  if (!rank || rank < 1 || rank > 32) return 'rgba(255,255,255,0.3)';
  const hue = Math.round((32 - rank) / 31 * 120);
  return `hsl(${hue}, 65%, 42%)`;
};

const SECTIONS = ['Offense', 'Defense'];

const StatsPanel = ({ awayTeam, homeTeam, awayStats, homeStats }) => {
  const awayMap = {};
  const homeMap = {};
  for (const s of (awayStats || [])) awayMap[`${s.section}__${s.label}`] = s;
  for (const s of (homeStats || [])) homeMap[`${s.section}__${s.label}`] = s;

  const allKeys = [...new Set([...(awayStats || []), ...(homeStats || [])].map(s => `${s.section}__${s.label}`))];

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 1, px: 0.5 }}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: 80, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>{awayTeam?.split(" ").pop()}</Typography>
        </Box>
        <Box sx={{ width: 80, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>{homeTeam?.split(" ").pop()}</Typography>
        </Box>
      </Box>
      {SECTIONS.map(section => {
        const rows = allKeys.filter(k => k.startsWith(section + '__'));
        if (!rows.length) return null;
        return (
          <Box key={section} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5, px: 0.5 }}>
              {section}
            </Typography>
            {rows.map(key => {
              const label = key.split('__')[1];
              const away = awayMap[key];
              const home = homeMap[key];
              return (
                <Box key={key} sx={{ display: "flex", alignItems: "center", py: 0.5, borderBottom: "1px solid rgba(255,255,255,0.04)", px: 0.5 }}>
                  <Typography sx={{ flex: 1, fontSize: 12, color: "text.secondary" }}>{label}</Typography>
                  {[away, home].map((s, i) => (
                    <Box key={i} sx={{ width: 80, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{s?.displayValue ?? '—'}</Typography>
                      {s?.rank && (
                        <Box sx={{
                          bgcolor: rankColor(s.rank),
                          color: "#fff",
                          fontSize: 9,
                          fontWeight: 700,
                          borderRadius: "4px",
                          px: 0.4,
                          py: 0.1,
                          minWidth: 18,
                          textAlign: "center",
                          lineHeight: "14px",
                        }}>
                          {s.rank}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const DepthChartList = ({ teamName, formations, injuries }) => {
  const injuryMap = {};
  for (const inj of (injuries || [])) {
    if (inj.name && INJURY_ABBR[inj.status]) injuryMap[inj.name] = inj.status;
  }

  return (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
      <img src={teamLogoSrc(teamName)} alt="" width={28} height={28} style={{ objectFit: "contain" }} />
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{teamName}</Typography>
    </Box>
    {!formations?.length ? (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
        No roster available
      </Typography>
    ) : (
      formations.map((formation, fi) => (
        <Box key={fi} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", mb: 0.5, letterSpacing: 0.5 }}>
            {formation.name}
          </Typography>
          {formation.positions.map((pos, pi) => {
            const injStatus = injuryMap[pos.player];
            const isBackup = pos.rank >= 2;
            return (
              <Box key={pi} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.4, borderBottom: "1px solid rgba(255,255,255,0.04)", opacity: isBackup ? 0.65 : 1 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 40 }}>{isBackup ? "" : pos.position}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {injStatus && (
                    <Box sx={{
                      bgcolor: INJURY_COLOR[injStatus],
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: "50%",
                      width: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {INJURY_ABBR[injStatus]}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: isBackup ? 11 : 12, fontWeight: isBackup ? 400 : 500, textAlign: "right" }}>{pos.player}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))
    )}
  </Box>
  );
};

const ATS_TYPE_NAMES = {
  atsOverall: "Overall ATS",
  atsFavorite: "Favorite ATS",
  atsUnderdog: "Underdog ATS",
  atsHome: "Home ATS",
  atsAway: "Away ATS",
  atsHomeFavorite: "Home Fav ATS",
  atsHomeUnderdog: "Home Dog ATS",
  atsAwayFavorite: "Away Fav ATS",
  atsAwayUnderdog: "Away Dog ATS",
};

const formatAts = (item) => {
  if (!item) return "—";
  const { wins, losses, pushes } = item;
  return pushes ? `${wins}-${losses}-${pushes}` : `${wins}-${losses}`;
};

const getAtsKeys = (isFavorite, isHome) => {
  const keys = ["atsOverall"];
  if (isFavorite) {
    keys.push("atsFavorite");
    if (isHome) keys.push("atsHome", "atsHomeFavorite");
    else keys.push("atsAway", "atsAwayFavorite");
  } else {
    keys.push("atsUnderdog");
    if (isHome) keys.push("atsHome", "atsHomeUnderdog");
    else keys.push("atsAway", "atsAwayUnderdog");
  }
  return keys;
};

const AtsPanel = ({ awayTeam, homeTeam, awayAts, homeAts, awaySpread, homeSpread }) => {
  const homeFav = homeSpread != null && awaySpread != null && +homeSpread < +awaySpread;
  const awayFav = awaySpread != null && homeSpread != null && +awaySpread < +homeSpread;
  if (!homeAts?.length && !awayAts?.length) return null;

  const buildMap = (items) => {
    const m = {};
    for (const item of (items || [])) m[item.type?.name] = item;
    return m;
  };
  const awayMap = buildMap(awayAts);
  const homeMap = buildMap(homeAts);
  const awayKeys = getAtsKeys(awayFav, false);
  const homeKeys = getAtsKeys(homeFav, true);

  const rows = [...new Set([...awayKeys, ...homeKeys])];

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, mb: 1, px: 0.5 }}>
        Against the Spread
      </Typography>
      <Box sx={{ display: "flex", mb: 0.5, px: 0.5 }}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ width: 80, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>{awayTeam?.split(" ").pop()}</Typography>
        </Box>
        <Box sx={{ width: 80, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>{homeTeam?.split(" ").pop()}</Typography>
        </Box>
      </Box>
      {rows.map(key => (
        <Box key={key} sx={{ display: "flex", alignItems: "center", py: 0.5, borderBottom: "1px solid rgba(255,255,255,0.04)", px: 0.5 }}>
          <Typography sx={{ flex: 1, fontSize: 12, color: "text.secondary" }}>{ATS_TYPE_NAMES[key] ?? key}</Typography>
          <Box sx={{ width: 80, textAlign: "center" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{awayKeys.includes(key) ? formatAts(awayMap[key]) : ""}</Typography>
          </Box>
          <Box sx={{ width: 80, textAlign: "center" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{homeKeys.includes(key) ? formatAts(homeMap[key]) : ""}</Typography>
          </Box>
        </Box>
      ))}
      <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.10)", mt: 2 }} />
    </Box>
  );
};

const formatMovementTime = (ts) => {
  const d = new Date(ts);
  const day = d.toLocaleString("en-US", { weekday: "short" });
  const time = d.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day} ${time}`;
};

const formatSpread = (val) => { const n = parseFloat(val); return n > 0 ? `+${n}` : `${n}`; };

const movDir = (from, to) => {
  const f = parseFloat(from), t = parseFloat(to);
  if (isNaN(f) || isNaN(t) || f === t) return null;
  return t > f ? 1 : -1;
};

const DirArrow = ({ dir, color }) => {
  if (!dir) return null;
  return (
    <Typography component="span" sx={{ fontSize: 11, ml: 0.5, color, lineHeight: 1 }}>
      {dir > 0 ? '▲' : '▼'}
    </Typography>
  );
};

const LineFeed = ({ movements, loading, homeSpread, awaySpread, over, homeTeam, awayTeam }) => {
  const [logOpen, setLogOpen] = useState(false);

  if (loading) return <PageLoader />;
  if (!movements.length) return (
    <Typography sx={{ color: "text.secondary", textAlign: "center", pt: 4, fontSize: 13 }}>
      No line movements recorded for this game yet.
    </Typography>
  );

  const sorted = [...movements].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Opening lines from first movement's `from` values (tracked per team, not per favorite/dog,
  // since the favorite can flip teams over time)
  const openingCol = {};
  for (const mov of sorted) {
    if (mov.type === "spread" && openingCol.homeSpread === undefined) {
      const homeN = parseFloat(mov.from?.home);
      const awayN = parseFloat(mov.from?.away);
      if (!isNaN(homeN)) openingCol.homeSpread = formatSpread(homeN);
      if (!isNaN(awayN)) openingCol.awaySpread = formatSpread(awayN);
    }
    if (mov.type === "total" && openingCol.over === undefined) {
      openingCol.over = mov.from != null ? String(mov.from) : null;
    }
  }

  // Current values
  const homeN = parseFloat(homeSpread);
  const awayN = parseFloat(awaySpread);
  const nowCol = {
    homeSpread: !isNaN(homeN) ? formatSpread(homeN) : null,
    awaySpread: !isNaN(awayN) ? formatSpread(awayN) : null,
    over: over != null ? String(over) : null,
  };

  const ROWS = [
    { key: "homeSpread", label: homeTeam ? `${homeTeam} Spread` : "Home Spread" },
    { key: "awaySpread", label: awayTeam ? `${awayTeam} Spread` : "Away Spread" },
    { key: "over",       label: "Over/Under" },
  ];

  // Build change log entries
  const logEntries = sorted.map(mov => {
    if (mov.type === "spread") {
      const toHomeN = parseFloat(mov.to?.home);
      const toAwayN = parseFloat(mov.to?.away);
      const homeTo = formatSpread(toHomeN);
      const awayTo = formatSpread(toAwayN);
      const fromHomeN = parseFloat(mov.from?.home);
      const fromAwayN = parseFloat(mov.from?.away);
      const homeFrom = !isNaN(fromHomeN) ? formatSpread(fromHomeN) : null;
      const awayFrom = !isNaN(fromAwayN) ? formatSpread(fromAwayN) : null;
      const homeLabel = homeTeam || "Home";
      const awayLabel = awayTeam || "Away";
      return {
        ts: mov.timestamp,
        label: "Spread",
        from: (homeFrom || awayFrom) ? `${awayLabel} ${awayFrom} / ${homeLabel} ${homeFrom}` : null,
        to: `${awayLabel} ${awayTo} / ${homeLabel} ${homeTo}`,
        dir: movDir(homeFrom, homeTo),
      };
    } else {
      return { ts: mov.timestamp, label: "Total", from: mov.from != null ? String(mov.from) : null, to: String(mov.to), dir: movDir(mov.from, mov.to) };
    }
  });

  return (
    <Box>
      {/* Summary: Opening → Now */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", mb: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ width: 90, textAlign: "center" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>Opening</Typography>
          </Box>
          <Box sx={{ width: 90, textAlign: "center" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}>Now</Typography>
          </Box>
        </Box>
        {ROWS.map(({ key, label }) => {
          const open = openingCol[key];
          const now = nowCol[key];
          const dir = movDir(open, now);
          const arrowColor = (key === "homeSpread" || key === "awaySpread") ? (dir < 0 ? "#4caf50" : "#f44336") : (dir > 0 ? "#4caf50" : "#f44336");
          return (
            <Box key={key} sx={{ display: "flex", alignItems: "center", py: 0.75, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
              </Box>
              <Box sx={{ width: 90, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13 }}>{open ?? "—"}</Typography>
              </Box>
              <Box sx={{ width: 90, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{now ?? "—"}</Typography>
                {dir && <DirArrow dir={dir} color={arrowColor} />}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Collapsible change log */}
      <Box
        onClick={() => setLogOpen(o => !o)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", py: 0.5, userSelect: "none" }}
      >
        <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>
          Change Log ({logEntries.length})
        </Typography>
        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{logOpen ? "▲ Hide" : "▼ Show"}</Typography>
      </Box>

      {logOpen && (
        <Box sx={{ mt: 0.5 }}>
          {logEntries.map((entry, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", py: 0.6, borderBottom: "1px solid rgba(255,255,255,0.04)", gap: 1 }}>
              <Typography sx={{ fontSize: 11, color: "text.disabled", minWidth: 80, flexShrink: 0 }}>
                {formatMovementTime(entry.ts)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary", minWidth: 44, flexShrink: 0 }}>{entry.label}</Typography>
              <Typography sx={{ fontSize: 11, flex: 1 }}>
                {entry.from ? <Typography component="span" sx={{ color: "text.disabled", fontSize: 11 }}>{entry.from} → </Typography> : null}
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 11 }}>{entry.to}</Typography>
                {entry.dir && <DirArrow dir={entry.dir} color={entry.dir > 0 ? "#4caf50" : "#f44336"} />}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const StevenGameInfo = ({ showStevenInfo, selectedGameId, setShowStevenInfo, homeTeam, awayTeam, homeSpread, awaySpread, over, canViewPublicBetting, publicBetting }) => {
  const [weatherData, setWeatherData] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [injuryLoading, setInjuryLoading] = useState(false);
  const [depthLoading, setDepthLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [atsLoading, setAtsLoading] = useState(false);
  const [injuries, setInjuries] = useState({ home: [], away: [] });
  const [depthChart, setDepthChart] = useState({ home: [], away: [] });
  const [teamStats, setTeamStats] = useState({ home: [], away: [] });
  const [atsData, setAtsData] = useState({ home: null, away: null });
  const [cityName, setCityName] = useState("");
  const [isIndoor, setIsIndoor] = useState(false);
  const [tab, setTab] = useState(0);
  const [lineMovements, setLineMovements] = useState([]);
  const [lineMovementsLoading, setLineMovementsLoading] = useState(false);

  const handleShowStevenInfo = () => {
    setShowStevenInfo(false);
  };

  const getGame = async () => {
    try {
      const response = await gameService.getGame(selectedGameId);
      const game = response.data?.[0];
      if (!game) throw new Error("Game not found");
      await populate(game);
    } catch (error) {
      console.error("Error fetching game:", error);
      setWeatherData([]);
      setWeatherLoading(false);
    }
  };

  // Fallback only — used when a game has no venue data (e.g. games stored before
  // venue tracking was added). Guesses the home team's usual stadium city, which is
  // wrong for neutral-site/international games, so prefer game.venue_city when present.
  const getCityFromTeam = (homeTeam) => {
    if (!homeTeam) return "";
    let home_team_location = homeTeam.split(" ")[0];
    if (homeTeam.split(" ").length > 2) {
      home_team_location = homeTeam.split(" ")[0] + " " + homeTeam.split(" ")[1];
    } 

    switch (home_team_location) {
      case "New England":
        return "Foxborough";
      case "Arizona":
        return "Glendale";
      case "Carolina":
        return "Charlotte";
      case "Minnesotta":
        return "Minneapolis";
      case "Tennessee":
        return "Nashville";
      case "New York":
        return "East Rutherford";
      case "Buffalo":
        return "Orchard Park"
      case "Dallas":
        return "Arlington"
      case "San Francisco":
        return "Santa Clara"
    }

    return home_team_location;
  };

  const getTimeZone = (city) => {
    const cityTimeZones = {
      "Foxborough": "America/New_York",
      "Orchard Park": "America/New_York",   
      "Miami": "America/New_York",
      "East Rutherford": "America/New_York",
      "Philadelphia": "America/New_York",
      "Washington": "America/New_York",
      "Chicago": "America/Chicago",
      "Detroit": "America/Detroit",
      "Green Bay": "America/Chicago",
      "Minneapolis": "America/Chicago",
      "Atlanta": "America/New_York",
      "Charlotte": "America/New_York",
      "New Orleans": "America/Chicago",
      "Tampa Bay": "America/New_York",
      "Arlington": "America/Chicago",
      "Houston": "America/Chicago",
      "Indianapolis": "America/Indiana/Indianapolis",
      "Jacksonville": "America/New_York",
      "Nashville": "America/Chicago",
      "Denver": "America/Denver", 
      "Kansas City": "America/Chicago",
      "Las Vegas": "America/Los_Angeles",
      "Los Angeles": "America/Los_Angeles",
      "Santa Clara": "America/Los_Angeles",
      "Seattle": "America/Los_Angeles",
      "Glendale": "America/Phoenix",
      "Pittsburgh": "America/New_York",
      "Baltimore": "America/New_York",
      "Cincinnati": "America/New_York",
      "Cleveland": "America/New_York",
      "Carolina": "America/New_York",
      "Charlotte": "America/New_York"
    };

    return cityTimeZones[city] || "Unknown city";
  }

  const getWeather = async (city, country, gameDate) => {
    try {
      if (!city || !gameDate) return [];

      // Resolve timezone for the actual venue city via server-side geocoding (works for
      // any city worldwide, including neutral-site/international venues).
      let timeZone = "UTC";
      try {
        const geoRes = await apiClient.get('/api/geocode', { params: { city, country } });
        timeZone = geoRes.data?.timezone || getTimeZone(city) || "UTC";
      } catch {
        timeZone = getTimeZone(city) || "UTC";
      }

      const localDate = new Date(new Date(gameDate).toLocaleString("en-US", { timeZone }));
      const formattedDate = localDate.toISOString().split("T")[0];
      const localHour = localDate.getHours();

      const res = await apiClient.get('/api/weather', {
        params: {
          city,
          country,
          date: formattedDate,
          commenceTime: gameDate,
        },
      });

      const allHours = res.data;
      if (!allHours?.length) return [];

      return allHours.slice(localHour, Math.min(localHour + 4, 24));
    } catch (error) {
      console.error("Error fetching weather:", error);
      return [];
    }
  };

  const populate = async (game) => {
    const { home_team: home, away_team: away, commence_time: gameDate } = game;
    try {
      setWeatherLoading(true);
      setInjuryLoading(true);
      setDepthLoading(true);
      setStatsLoading(true);
      setAtsLoading(true);

      // Prefer the actual game site reported by ESPN (handles neutral-site and
      // international games); fall back to guessing from the home team for older
      // games stored before venue tracking was added.
      const city = game.venue_city || getCityFromTeam(home);
      const country = game.venue_country && game.venue_country !== "USA" ? game.venue_country : undefined;
      const indoor = game.venue_indoor === true;
      setCityName(game.venue_name ? `${city} (${game.venue_name})` : city);
      setIsIndoor(indoor);

      const [next4Hours, injuryRes, depthRes, statsRes, atsRes] = await Promise.all([
        indoor ? Promise.resolve([]) : getWeather(city, country, gameDate),
        apiClient.get(`/api/injuries?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}&commenceTime=${encodeURIComponent(gameDate)}`)
          .then(r => r.data || { home: [], away: [] })
          .catch(() => ({ home: [], away: [] })),
        apiClient.get(`/api/depthchart?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}&commenceTime=${encodeURIComponent(gameDate)}`)
          .then(r => r.data || { home: [], away: [] })
          .catch(() => ({ home: [], away: [] })),
        apiClient.get(`/api/stats?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}`)
          .then(r => r.data || { home: [], away: [] })
          .catch(() => ({ home: [], away: [] })),
        apiClient.get(`/api/ats?home=${encodeURIComponent(home)}&away=${encodeURIComponent(away)}&homeSpread=${encodeURIComponent(homeSpread ?? '')}&awaySpread=${encodeURIComponent(awaySpread ?? '')}`)
          .then(r => r.data || { home: null, away: null })
          .catch(() => ({ home: null, away: null })),
      ]);
      setWeatherData(next4Hours);
      setInjuries(injuryRes);
      setDepthChart(depthRes);
      setTeamStats(statsRes);
      setAtsData(atsRes || { home: null, away: null });
    } catch (error) {
      console.error("Error populating game info:", error);
      setWeatherData([]);
      setInjuries({ home: [], away: [] });
      setDepthChart({ home: [], away: [] });
      setAtsData({ home: null, away: null });
    } finally {
      setWeatherLoading(false);
      setInjuryLoading(false);
      setDepthLoading(false);
      setStatsLoading(false);
      setAtsLoading(false);
    }
  };

  useEffect(() => {
    if (showStevenInfo) {
      setTab(0);
      getGame();
      setLineMovementsLoading(true);
      gameService.getLineMovements(selectedGameId)
        .then(r => setLineMovements(r.data || []))
        .catch(() => setLineMovements([]))
        .finally(() => setLineMovementsLoading(false));
    } else {
      setWeatherData([]);
      setInjuries({ home: [], away: [] });
      setDepthChart({ home: [], away: [] });
      setTeamStats({ home: [], away: [] });
      setAtsData({ home: null, away: null });
      setWeatherLoading(true);
      setIsIndoor(false);
      setLineMovements([]);
    }
  }, [showStevenInfo, selectedGameId]);

  return (
    <Dialog open={showStevenInfo} onClose={handleShowStevenInfo} maxWidth="sm" fullWidth>
      {(awayTeam || homeTeam || over != null) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, px: 2, pt: 1.5, pb: 0.5, flexWrap: "wrap" }}>
          {(() => {
            const awayFav = awaySpread != null && homeSpread != null && +awaySpread < +homeSpread;
            const homeFav = homeSpread != null && awaySpread != null && +homeSpread < +awaySpread;
            const spreadStr = (val) => +val > 0 ? `+${val}` : `${val}`;
            return (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {awayTeam}{awayFav && awaySpread != null ? ` (${spreadStr(awaySpread)})` : ""}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>@</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {homeTeam}{homeFav && homeSpread != null ? ` (${spreadStr(homeSpread)})` : ""}
                </Typography>
                {over != null && (
                  <>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>·</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Total: {over}</Typography>
                  </>
                )}
              </>
            );
          })()}
        </Box>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
        <Tab label="Weather" />
        <Tab label="Depth Chart" />
        <Tab label="Lines" />
        <Tab label="Stats" />
        {canViewPublicBetting && <Tab label="Public Betting" />}
      </Tabs>
      <DialogContent dividers>
        {tab === 0 && (
          weatherLoading ? (
            <PageLoader />
          ) : isIndoor ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>Venue: {cityName}</Typography>
              <Typography>This game is played indoors — weather does not apply.</Typography>
            </>
          ) : weatherData.length ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>Note: time displayed in local time</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>City: {cityName}</Typography>
              <Grid container spacing={2}>
                {weatherData.map((hour) => {
                  const [hh, mm] = hour.time.split("T")[1].split(":");
                  const hour12 = ((+hh + 11) % 12) + 1;
                  const ampm = +hh >= 12 ? "PM" : "AM";
                  const formattedTime = `${hour12}:${mm} ${ampm}`;
                  return (
                    <Grid item xs={12} key={hour.time}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2"><b>Time:</b> {formattedTime}</Typography>
                          <Typography variant="body2"><b>Temp:</b> {hour.temperature}°F</Typography>
                          <Typography variant="body2"><b>Wind:</b> {hour.wind} mph</Typography>
                          <Typography variant="body2"><b>Precipitation:</b> {hour.precipitation}%</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Typography>No weather data available</Typography>
          )
        )}
        {tab === 1 && (
          depthLoading ? (
            <PageLoader />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <DepthChartList teamName={awayTeam} formations={depthChart.away} injuries={injuries.away} />
              <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.08)" }} />
              <DepthChartList teamName={homeTeam} formations={depthChart.home} injuries={injuries.home} />
            </Box>
          )
        )}
        {tab === 2 && (
          <LineFeed movements={lineMovements} loading={lineMovementsLoading} homeSpread={homeSpread} awaySpread={awaySpread} over={over} homeTeam={homeTeam} awayTeam={awayTeam} />
        )}
        {tab === 3 && (
          <>
            {statsLoading ? <PageLoader /> : (
              <>
                <AtsPanel
                  awayTeam={awayTeam}
                  homeTeam={homeTeam}
                  awayAts={atsData.away}
                  homeAts={atsData.home}
                  awaySpread={awaySpread}
                  homeSpread={homeSpread}
                />
                <StatsPanel awayTeam={awayTeam} homeTeam={homeTeam} awayStats={teamStats.away} homeStats={teamStats.home} />
              </>
            )}
          </>
        )}
        {canViewPublicBetting && tab === 4 && (
          <PublicBettingConsensus consensusData={publicBetting} homeTeam={homeTeam} awayTeam={awayTeam} />
        )}
      </DialogContent>
      <DialogActions>
        <StevenButton onClick={handleShowStevenInfo}>Close</StevenButton>
      </DialogActions>
    </Dialog>
  );
};

export default StevenGameInfo;
